export type ContentBlock =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'image'; src: string; alt: string; caption?: string }
  | { type: 'callout'; text: string; linkText: string; linkUrl: string };

export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  category: 'Roam Weather' | 'Roam Office Tracker';
  readTime: string;
  content: ContentBlock[];
}

export const blogPosts: BlogPost[] = [
  {
    slug: 'smoke-forecasts-for-any-trailhead',
    title: 'Smoke Forecasts Just Landed in Roam Weather — For Any Point on Earth',
    description:
      "Wildfire smoke can wreck a trip just as easily as rain or lightning. Roam Weather now shows hourly smoke and air quality forecasts up to 96 hours out — for any trailhead, summit, or point location in the US, Canada, Europe, and beyond.",
    date: 'July 2026',
    category: 'Roam Weather',
    readTime: '3 min read',
    content: [
      {
        type: 'p',
        text: "If you've ever shown up at a trailhead to find the valley choked with smoke — or cancelled a summit push because the AQI spiked overnight — you know that wildfire smoke is one of the hardest conditions to plan around. It doesn't show up in a typical 7-day forecast, and by the time you can see it or smell it, you're already there. That changes today: Roam Weather now includes smoke and air quality forecasting for any location you save.",
      },
      {
        type: 'h2',
        text: 'Smoke Forecasts for Any Point Location, Worldwide',
      },
      {
        type: 'p',
        text: "This isn't limited to a handful of major cities or national parks. Roam Weather's smoke forecasting works for any point location you can drop a pin on — a specific trailhead parking lot, a mountain summit, a backcountry campsite, or a climbing crag. Coverage spans the United States, Canada, and North America broadly, plus Europe, so whether you're planning a trip to the Cascades, the Bugaboos, or the Alps, you can check exactly how smoke is expected to move through that spot.",
      },
      {
        type: 'image',
        src: '/images/smoke-forecast.png',
        alt: 'Roam Weather list view showing 7-day smoke forecast pills for a specific trailhead location',
        caption: 'Every saved location gets its own smoke forecast strip, right alongside temperature and precipitation.',
      },
      {
        type: 'h2',
        text: 'Hourly Detail, Not Just a Daily Guess',
      },
      {
        type: 'p',
        text: "Smoke conditions can swing dramatically within a single day — clear in the morning, hazardous by nightfall as inversions trap smoke in a valley. That's why Roam Weather breaks the forecast down hour by hour, with an AQI reading and category (Good, Moderate, Sensitive, Unhealthy, Very Unhealthy, or Hazardous) for every hour, not just a single number for the whole day.",
      },
      {
        type: 'image',
        src: '/images/smoke-forecast-hourly.png',
        alt: 'Hourly smoke AQI detail view with a scrollable hour-by-hour timeline and color-coded AQI scale',
        caption: 'Tap into any day to see the hour-by-hour AQI trend, so you know exactly when conditions turn.',
      },
      {
        type: 'h2',
        text: 'Plan Around It Before You Even Leave the House',
      },
      {
        type: 'p',
        text: "Because the forecast reaches out to 96 hours — a full 4 days — you're not just checking smoke conditions for today. You can see whether tomorrow night's camp will be clear or smoky, whether a summit push at sunrise in three days is worth planning around, or whether it's smarter to shift a trip a day earlier or later to dodge a smoke-filled window entirely.",
      },
      {
        type: 'image',
        src: '/images/smoke-forecast-detail.png',
        alt: 'Roam Weather forecast list showing 4-day smoke AQI values alongside temperature and precipitation for a trailhead',
        caption: 'Smoke AQI shows up right next to the rest of your forecast — no separate app or tab required.',
      },
      {
        type: 'h2',
        text: 'Works Everywhere You Already Use Roam Weather',
      },
      {
        type: 'p',
        text: "Smoke forecasting isn't a separate tool bolted on — it's built directly into the same list and map views you already use to compare locations. Save a collection of trailheads for a trip and you'll see smoke AQI right alongside temperature, wind, and precipitation for every one of them, so a hazy air quality day doesn't sneak up on you the way it used to.",
      },
      {
        type: 'h2',
        text: 'Check It Before Your Next Trip',
      },
      {
        type: 'p',
        text: "Roam Weather is free to start, with up to 3 saved locations and no account required. Premium unlocks unlimited saved locations for $1.29/month (or $5.99/year, or a one-time $11.99 for lifetime access) — all plans are non-recurring, so you only pay when you need it.",
      },
      {
        type: 'callout',
        text: 'Check the smoke forecast for your next trip.',
        linkText: 'Try Roam Weather free at weather.roamapps.com',
        linkUrl: 'https://weather.roamapps.com',
      },
    ],
  },
  {
    slug: 'roam-weather-for-rock-climbers',
    title: "The Rock Climber's Guide to Smarter Crag Weather Planning",
    description:
      "Stop toggling between weather tabs. Roam Weather lets you compare forecasts for all your favorite crags side by side — with an idealness score tailored to climbing conditions.",
    date: 'March 2026',
    category: 'Roam Weather',
    readTime: '4 min read',
    content: [
      {
        type: 'p',
        text: "Every climber knows the drill. It's Thursday afternoon, and you're trying to decide: do we head to Vantage this weekend, or does Washington Pass look better? So you open up NOAA, check Vantage, mentally note the Saturday forecast, then open a new tab for Washington Pass, try to hold both forecasts in your head, and attempt to compare two days across two locations. It's tedious — and it's easy to miss the perfect window.",
      },
      {
        type: 'h2',
        text: 'The Problem with Checking Crags One at a Time',
      },
      {
        type: 'p',
        text: "Most weather apps are designed for where you are right now, not where you want to go — and especially not for comparing multiple destinations. When you're planning a crag day, you're not asking \"what's the weather in Seattle?\" You're asking \"which of my three potential spots will have the best conditions on Saturday, and is Sunday morning worth sticking around for?\"",
      },
      {
        type: 'p',
        text: 'That question deserves a better tool.',
      },
      {
        type: 'h2',
        text: 'How Roam Weather Is Built for Climbers',
      },
      {
        type: 'p',
        text: "Roam Weather was built by a climber, for climbers — starting with exactly that Vantage vs. Washington Pass dilemma. Here's how it changes the game:",
      },
      {
        type: 'h3',
        text: 'Save All Your Crags in One Place',
      },
      {
        type: 'p',
        text: 'Add all your go-to crags, bouldering areas, and approach trailheads to Roam Weather and organize them into collections — "PNW Sport Climbing," "Desert Trad," "Summer Alpine," whatever makes sense for your climbing season. No more bookmarking individual forecast URLs.',
      },
      {
        type: 'h3',
        text: 'Compare Forecasts Side by Side',
      },
      {
        type: 'p',
        text: 'The list view shows every saved location with a scrollable 7-day forecast strip. See temperature, precipitation chance, and wind at a glance for every crag — all on one screen, no tab-switching required. The map view plots all your spots with color-coded pins so you can visualize which region looks best.',
      },
      {
        type: 'h3',
        text: 'Get an Idealness Score for Climbing',
      },
      {
        type: 'p',
        text: "This is the feature that makes Roam Weather different. Define what \"good climbing weather\" means for you — preferred temperature range (say, 45–65°F), maximum wind speed, acceptable precipitation chance — and every forecast day gets scored from 0–100%. Instantly see that Saturday at Vantage is 92% ideal while Washington Pass is sitting at 45%. Decision made.",
      },
      {
        type: 'h3',
        text: 'Elevation-Adjusted Temperatures',
      },
      {
        type: 'p',
        text: "NOAA forecasts are issued for a grid elevation that can vary significantly from your actual crag elevation. Roam Weather adjusts temperatures to the actual elevation of your saved location. If your approach tops out at 7,000 feet and the nearest NOAA grid is at 3,500 feet, that difference matters — especially for a shoulder-season alpine route.",
      },
      {
        type: 'h3',
        text: 'Sunlight-Adjusted "Feels Like" Temps',
      },
      {
        type: 'p',
        text: "Any experienced climber knows that 45°F on a sunny granite wall feels completely different from 45°F under a cloudy sky. Roam Weather's idealness scoring accounts for solar radiation — so a clear, sunny day at the crag gets scored more favorably than a grey one at the same temperature.",
      },
      {
        type: 'h3',
        text: 'Offline Access at the Crag',
      },
      {
        type: 'p',
        text: "Once you've checked the forecast at the trailhead parking lot, you don't want to lose it when you lose cell signal in the canyon. Install Roam Weather as a PWA (Progressive Web App) on iOS or Android and your cached forecasts are available even without cell service.",
      },
      {
        type: 'h3',
        text: 'Share Forecasts with Your Crew',
      },
      {
        type: 'p',
        text: 'Trying to convince your partners to commit to the trip? Send them a shareable forecast link for any coordinates — no account needed to view it. Perfect for the "should we go?" group chat.',
      },
      {
        type: 'h2',
        text: 'Try It on Your Next Crag Trip',
      },
      {
        type: 'p',
        text: "Roam Weather is free to start — save up to 6 locations with no account required. For most weekend warriors with a short list of favorite crags, that's plenty. If you want to save your full tick list, premium is $1.29/month (or $5.99/year, or $11.99 for a lifetime unlock) — all plans are non-recurring, so you only pay when you need it.",
      },
      {
        type: 'p',
        text: 'Available on the web at weather.roamapps.com (installable as a PWA) and on iOS from the App Store. Android users can install the web app as a PWA.',
      },
      {
        type: 'callout',
        text: 'Plan your next crag day with Roam Weather.',
        linkText: 'Try it free at weather.roamapps.com',
        linkUrl: 'https://weather.roamapps.com',
      },
    ],
  },
  {
    slug: 'backpacking-weather-planning-with-roam',
    title: "Better Backpacking Trips Start with Better Weather Planning",
    description:
      "When you're committing to a 3-day route in the backcountry, weather windows matter. Roam Weather helps backpackers compare trailheads, plan multi-day trips, and access forecasts offline.",
    date: 'March 2026',
    category: 'Roam Weather',
    readTime: '4 min read',
    content: [
      {
        type: 'p',
        text: "Picking the right weekend for a backpacking trip involves more than just checking whether it'll rain. You're scanning for that elusive combination: dry days, manageable temps for the elevation, no afternoon thunderstorms on the exposed ridge, and maybe a clear night for stargazing. And if you have multiple routes in mind, you're trying to compare all of that across different locations at the same time.",
      },
      {
        type: 'h2',
        text: 'The Challenge of Multi-Location Trip Planning',
      },
      {
        type: 'p',
        text: "Standard weather apps show you one place at a time. But when you're deciding between three potential weekend trips — a ridge route in the Cascades, a desert canyon in Utah, or a coastal trail in Oregon — you're making a comparative decision. You need to see all of those forecasts together, not in separate tabs.",
      },
      {
        type: 'h2',
        text: 'How Roam Weather Helps Backpackers',
      },
      {
        type: 'h3',
        text: 'Organize Your Routes into Collections',
      },
      {
        type: 'p',
        text: "Add trailheads, campsites, and summits to Roam Weather and group them into collections that match your trip planning — \"PNW Summer,\" \"Fall Desert Routes,\" \"Winter Low-Elevation.\" When a trip idea comes up, all the locations you'd consider are already saved and ready to compare.",
      },
      {
        type: 'h3',
        text: 'See the Full 7-Day Forecast for Every Location',
      },
      {
        type: 'p',
        text: 'The list view gives every saved location a scrollable 7-day forecast strip. At a glance, you can see which trailhead has the best window — not just for one day, but for the full trip duration. The map view overlays all your spots with color-coded forecast pins so you can visualize which region looks best.',
      },
      {
        type: 'h3',
        text: "Set Your Own 'Good Weather' Criteria",
      },
      {
        type: 'p',
        text: "Every backcountry trip has different weather requirements. A desert slot canyon trip is very different from a high-alpine route. With Roam Weather's idealness score, you define what good weather means for each activity: your preferred temperature range, maximum wind speed, and acceptable precipitation chance. Each forecast day gets a score from 0–100%, making it easy to spot the best windows at a glance.",
      },
      {
        type: 'h3',
        text: 'Drill Into Hourly Details for Critical Days',
      },
      {
        type: 'p',
        text: "For summit days or river crossings where timing matters, tap into any location's full hourly breakdown: temperature, wind, precipitation probability, humidity, and dewpoint. Know whether the afternoon thunderstorms typically roll in at 1pm or 3pm before you commit to a route.",
      },
      {
        type: 'h3',
        text: 'Elevation-Adjusted Forecasts for Alpine Routes',
      },
      {
        type: 'p',
        text: "NOAA forecasts are issued at a grid elevation that may be thousands of feet below your actual destination. Roam Weather adjusts temperatures to the actual elevation of each saved location — critical for high routes where that 3,000-foot difference can mean the gap between rain and snow, or between a comfortable camp and a miserable one.",
      },
      {
        type: 'h3',
        text: 'Access Forecasts Offline in the Backcountry',
      },
      {
        type: 'p',
        text: "Cell service at the trailhead isn't guaranteed. Install Roam Weather as a PWA or iOS app and your cached forecasts are available without an internet connection — so you can check the forecast from your tent at 5am before deciding whether to push for the summit.",
      },
      {
        type: 'h2',
        text: 'Plan Your Next Adventure',
      },
      {
        type: 'p',
        text: "Roam Weather is free to start with up to 3 saved locations. For trip planning across multiple routes and regions, premium unlocks unlimited locations at $1.29/month (or $5.99/year, or a one-time $11.99 for lifetime access). All plans are non-recurring — pay when you need it.",
      },
      {
        type: 'p',
        text: "Roam Weather currently uses NOAA's forecast APIs, so it covers the contiguous United States. If you're planning Canadian routes like the Bugaboos or Squamish, international support is on the roadmap.",
      },
      {
        type: 'callout',
        text: 'Start planning your next backpacking trip.',
        linkText: 'Try Roam Weather free at weather.roamapps.com',
        linkUrl: 'https://weather.roamapps.com',
      },
    ],
  },
  {
    slug: 'microsoft-employees-best-8-of-12-weeks-office-tracker',
    title: 'How Microsoft Employees Can Track Their "Best 8 of 12 Weeks" Office Requirement',
    description:
      "Microsoft's return-to-office policy requires employees to meet in-office minimums on a rolling 12-week basis. Roam Office Tracker makes it easy to log office days, see your compliance status, and plan ahead so you always know how long you can be gone.",
    date: 'March 2026',
    category: 'Roam Office Tracker',
    readTime: '5 min read',
    content: [
      {
        type: 'p',
        text: "Microsoft's return-to-office policy has introduced a nuanced compliance model for many employees: rather than requiring the same number of office days every single week, compliance is evaluated on a rolling basis. The specific requirement — and how it's tracked — can make it genuinely difficult to know at any given moment exactly how much flexibility you have for remote work.",
      },
      {
        type: 'h2',
        text: 'Understanding the "Best 8 of 12 Weeks" Framework',
      },
      {
        type: 'p',
        text: "Under this model, compliance is measured across a rolling 12-week window. Employees need to meet the in-office minimum (typically 3 days per week) in at least 8 of those 12 weeks. That means you have room for up to 4 \"flex weeks\" in any 12-week span — weeks where you might work fully remote, travel, or take an extended work-from-home stretch.",
      },
      {
        type: 'p',
        text: "The challenge? Keeping track of it in real time. Without a dedicated tool, you're either checking a spreadsheet, counting back through your calendar, or hoping you haven't burned through your flex weeks without realizing it. That last option has a way of producing surprises at the worst moments — like when you want to take a fully remote week and aren't sure if you can afford it.",
      },
      {
        type: 'h2',
        text: 'How Roam Office Tracker Solves This',
      },
      {
        type: 'p',
        text: "Roam Office Tracker was designed specifically for rolling-window compliance tracking. Here's how it works for Microsoft employees:",
      },
      {
        type: 'h3',
        text: 'Log Your Office Days in Seconds',
      },
      {
        type: 'p',
        text: "The app's calendar interface makes it fast to mark which days you were in the office. Check off days as you go, or catch up at the end of the week — either way, it takes just a few taps.",
      },
      {
        type: 'h3',
        text: 'See Your Compliance Status at a Glance',
      },
      {
        type: 'p',
        text: "The dashboard shows you exactly where you stand in your current 12-week window: how many compliant weeks you've had, how many flex weeks you've used, and how many remain. No more mental math or spreadsheet lookups — you can see your standing in seconds.",
      },
      {
        type: 'h3',
        text: 'Know Exactly How Long You Can Be Gone',
      },
      {
        type: 'p',
        text: 'This is the question that drives most people to track their office days in the first place: "I want to work remotely for two weeks — can I do it?" The app\'s compliance view lets you see your current rolling window so you know whether you have the flex weeks available. If you\'ve been exceeding the minimum for several weeks, you can see exactly how much runway you have for a fully remote stretch.',
      },
      {
        type: 'h3',
        text: 'Plan Future Weeks with the Calendar View',
      },
      {
        type: 'p',
        text: "The calendar planning feature lets you mark upcoming days as in-office or remote before they happen. Coordinate around upcoming travel, holidays, or planned remote work — and see in real time how your planned schedule will affect your compliance status before you commit to it.",
      },
      {
        type: 'h3',
        text: "Fully Configurable for Your Team's Policy",
      },
      {
        type: 'p',
        text: 'The "best 8 of 12 weeks with 3 days per week" model is the default configuration, but Roam Office Tracker is fully adjustable. Whether your team tracks a different minimum number of in-office days, uses a different rolling window length, or requires a different compliance threshold, you can configure the settings to match your actual policy.',
      },
      {
        type: 'h2',
        text: 'Why a Dedicated Tool Makes a Difference',
      },
      {
        type: 'p',
        text: "The rolling-window model is deliberately designed to give employees flexibility — but only if they actually use it strategically. Without visibility into your current compliance status, that flexibility disappears into anxiety and guesswork. Roam Office Tracker gives you the visibility to use your flex weeks intentionally: to plan a longer trip, take a fully remote stretch when life gets busy, or simply say yes to a last-minute travel opportunity without stressing about where you stand.",
      },
      {
        type: 'h2',
        text: 'Get Started with Roam Office Tracker',
      },
      {
        type: 'p',
        text: "Roam Office Tracker is available at office.roamapps.com. It's free to start, and takes just a few minutes to set up and configure for your team's specific policy.",
      },
      {
        type: 'callout',
        text: 'Take the guesswork out of your office day tracking.',
        linkText: 'Try Roam Office Tracker at office.roamapps.com',
        linkUrl: 'https://office.roamapps.com',
      },
    ],
  },
];

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((post) => post.slug === slug);
}
